import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as staffService from "../../services/subAdmin.services";
import CustomPagination from "../../common/custompagination";
import Sorting from "../../common/sorting";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  capitalizeFirstLetter, formateDateWithMonth,
  handleServerValidations, TrimText, usePermission,
} from "../../../../utils/commonfunction";
import CustomRangepicker from "../../common/rangepicker";
import { Placeholder, Tooltip, Whisper } from "rsuite";

const SubAdminTable = () => {
  const { can } = usePermission();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState(false);
  const [datalength, setDataLength] = useState("");
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const [resetdate, setResetDate] = useState(false);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Sub Admins", url: "" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(true);
      const formData = new FormData();
      formData.append("page", params.pgno);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);
      staffService.List(formData).then((data) => {
        setDataLength(data?.data?.total_records || "");
        setList(data?.data?.list || []);
        setPage(data?.data?.page || 1);
        setLoader(false);
      }).catch(() => setLoader(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [location, statsupdate, sorting, search, globalsearch, params.pgno]);

  function DeleteFunction(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        const ids = Array.isArray(data) ? data : [data];
        staffService.Delete({ o_id: JSON.stringify(ids) }).then((response) => {
          setStatusUpdate(!statsupdate);
          Swal.fire({ icon: response.success ? "success" : "error", text: response.success ? response.message : handleServerValidations(response), ...SWAL_SETTINGS });
        });
      }
    });
  }

  const ChangeStatus = (data, Num) => {
    const formData = new FormData();
    const ids = Array.isArray(data) ? data : [data];
    formData.append("o_id", JSON.stringify(ids));
    formData.append("status", Num);
    Swal.fire({
      customClass: "swal-wide", title: t("msg_are_you_sure"), icon: "warning",
      showCancelButton: true, confirmButtonColor: "#403fad", cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes"), cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        staffService.Status(formData).then((response) => {
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
            setStatusUpdate(!statsupdate);
          } else {
            Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
          }
        });
      }
    });
  };

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    setSorting({ order: e.target.classList.contains("assc") ? -1 : 1, column });
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) sr[key] = value;
    else delete sr[key];
    setSearch(sr);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    setSearch({});
    setResetDate(!resetdate);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">Sub Admin Management</h6>
              <div className="d-flex align-items-center">
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input type="search" className="form-control" value={globalsearch}
                      placeholder={t("input_placeholder_search")} onChange={(e) => setGlobalSearch(e.target.value)} />
                  </div>
                </div>
                <div className="me-3">
                  <CustomRangepicker GetDateRange={(e) => prepareSearch("createdAt", e)} resetdate={resetdate} />
                </div>
                <button type="reset" onClick={resetFilter} className="btn ripple btn-main-primary signbtn me-2">
                  {t("btn_reset_filter")}
                </button>
                {can("/admin/staff/add") && (
                  <button className="btn ripple btn-main-primary signbtn" onClick={() => navigate("/admin/staff/add")}>
                    {t("btn_add_new")}
                  </button>
                )}
              </div>
            </div>

            {loader ? (
              <Placeholder.Paragraph className="w-100" rowHeight={30} rowSpacing={5} active rows={7} />
            ) : (
              <>
                <div className="table-responsive min-h-400">
                  <table className="table table-bordered card-table text-nowrap w-100">
                    <thead>
                      <tr>
                        <th>{t("list_heading_sno")}</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th className="status_head">{t("list_heading_status")}</th>
                        <th className="created_head">
                          <div className="sorting_column">
                            <span>{t("list_heading_created_date")}</span>
                            <Sorting sort={sorting} handleSort={handleSort} defaultSorting={defaultSorting} column="createdAt" />
                          </div>
                        </th>
                        {(can("/admin/staff/view") || can("/admin/staff/edit") || can("/admin/staff/delete")) && (
                          <th className="action_head">{t("list_heading_action")}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {list.length ? list.map((row, i) => (
                        <tr key={i}>
                          <td>{(page - 1) * itemperpage + i + 1}</td>
                          <td>{row?.first_name ? TrimText(row.first_name, 50) : "N/A"}</td>
                          <td>{row?.last_name ? TrimText(row.last_name, 50) : "N/A"}</td>
                          <td>{row?.email ? TrimText(row.email, 50) : "N/A"}</td>
                          <td>
                            <label className="custom-switch cp">
                              <input className="custom-switch-input cp" type="checkbox"
                                checked={row?.status === 1}
                                onChange={() => ChangeStatus(row?._id, row?.status === 1 ? 0 : 1)} />
                              <span className="custom-switch-indicator cp"></span>
                            </label>
                          </td>
                          <td>{row.createdAt ? formateDateWithMonth(row.createdAt) : "N/A"}</td>
                          {(can("/admin/staff/view") || can("/admin/staff/edit") || can("/admin/staff/delete")) && (
                            <td>
                              <div className="btn-toolbar table-actions">
                                <div className="btn-group btn-group-sm">
                                  {can("/admin/staff/view") && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>View</Tooltip>}>
                                      <button type="button" className="btn view" onClick={() => navigate(`/admin/staff/view/${row._id}`)}>
                                        <i className="ri-eye-fill"></i>
                                      </button>
                                    </Whisper>
                                  )}
                                  {can("/admin/staff/edit") && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>Edit</Tooltip>}>
                                      <button type="button" className="btn edit" onClick={() => navigate(`/admin/staff/${params.pgno}/edit/${row._id}`)}>
                                        <i className="ri-pencil-fill"></i>
                                      </button>
                                    </Whisper>
                                  )}
                                  {can("/admin/staff/delete") && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>Delete</Tooltip>}>
                                      <button type="button" className="btn delete" onClick={() => DeleteFunction(row?._id)}>
                                        <i className="ri-delete-bin-6-fill"></i>
                                      </button>
                                    </Whisper>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      )) : (
                        <tr><td colSpan={7} className="text-center">{t("msg_no_records")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div><b>{t("msg_total_records")} : {datalength || "0"}</b></div>
                {datalength && datalength > 0 ? (
                  <CustomPagination datalength={datalength} itemperpage={itemperpage} currentPage={page}
                    setPage={setPage} pageRoute={[{ name: "", path: "/admin/staff/list" }]} />
                ) : ""}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubAdminTable;
